import { createColumnHelper, type HeaderContext } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import clsx from "clsx";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { Info } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { CUSTOMER_LIST_QUERY_KEY } from "../../helpers/hooks/use-customer-list";
import type {
  CustomerRow,
  CustomerListResponse,
  ProductCapsule,
} from "../../types/crm";
import { PRODUCT_CAPSULE_LABELS } from "../../types/crm";
import { deriveCardStatus } from "./card-status";
import {
  updateTestFlag,
  updateCarr,
  updateProductCapsules,
} from "../../helpers/api/crm";
import { HealthBandChip } from "./HealthBandChip";

// ---------------------------------------------------------------------------
// Header tooltip helper — every column header gets one of these so a CS rep
// or finance reader can hover and learn (a) the data source, (b) the formula,
// (c) the fallback when the primary signal is missing. Source-of-truth for
// these explanations is the actual code: customer-list.service.ts SQL,
// users.service.ts:fetchDayMetrics, marketing-metrics-snapshot.service, and
// stripe-subscription-sync.service. Update both when formulas change.
// ---------------------------------------------------------------------------
const headerTip = (label: string, tooltip: string) => {
  // The render function is what react-table calls. We also attach the
  // plain-string `label` so non-rendering consumers (e.g. CSV export) can
  // read the human-readable header without traversing JSX.
  const render = () => (
  <Tippy
    content={
      <div className="text-[12px] leading-snug max-w-[280px] whitespace-pre-line py-1 text-content-primary">
        {tooltip}
      </div>
    }
    theme="autumn-light"
    placement="top"
    delay={[150, 0]}
    duration={[120, 80]}
    arrow={true}
    interactive={false}
  >
    <span className="inline-flex items-center gap-1 cursor-help align-middle">
      <span className="leading-none">{label}</span>
      <Info
        className="w-3 h-3 text-content-tertiary shrink-0 flex-none"
        strokeWidth={2}
        aria-hidden
      />
    </span>
  </Tippy>
  );
  return Object.assign(render, { label });
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

/** Takes a decimal fraction (0.13 = 13%) and renders a whole-number percentage. */
function formatPercent(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}

// ---------------------------------------------------------------------------
// Profit column — multi-path computation + tooltip explanation
// ---------------------------------------------------------------------------

/** Default commission rate applied when a customer has no rate configured
 *  yet (typical onboarding state). Lets us show a meaningful estimated
 *  profit instead of leaving the column blank for weeks. Never overrides
 *  an explicit `commissionRate = 0` — that's a deliberate "no commission"
 *  signal (free pilots etc.). */
const PROFIT_DEFAULT_RATE = 0.13;

type ProfitState = "real" | "estimated" | "pre_billing" | "no_signal";

interface ProfitInfo {
  state: ProfitState;
  value: number | null;
  /** Headline formula, e.g. "MRR − Ad Spend" */
  formula: string;
  /** Multi-line input breakdown rendered in the hover tooltip */
  breakdown: string[];
  /** Suffix shown next to the number, e.g. "(est)" */
  suffix: string | null;
}

function fmtUsd0(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtUsd2(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function computeProfitInfo(row: CustomerRow): ProfitInfo {
  const lastCommBill = row.billing.lastCommissionBill ?? 0;
  const fixedFee = row.billing.fixedFeeMonthly ?? 0;
  const rateConfigured = row.contract.commissionRate; // null | number
  const bookingsValueUsd = row.marketing.bookingsValueUsd30d ?? 0;
  const spend = row.marketing.spend30d;

  const hasRevenueData = lastCommBill > 0 || fixedFee > 0;

  // Path 1 — real revenue line exists. Use MRR − spend regardless of spend value.
  if (hasRevenueData) {
    const revenue = lastCommBill + fixedFee;
    const value = revenue - (spend ?? 0);
    const breakdown = [
      `Last paid commission: ${fmtUsd2(lastCommBill)}`,
      `Monthly fixed fee: ${fmtUsd2(fixedFee)}`,
      `Total revenue (MRR): ${fmtUsd2(revenue)}`,
      `Ad spend (30d): ${spend != null ? fmtUsd2(spend) : "—"}`,
      `Profit = ${fmtUsd2(revenue)} − ${fmtUsd2(spend ?? 0)} = ${fmtUsd2(value)}`,
    ];
    return {
      state: "real",
      value,
      formula: "MRR − Ad Spend",
      breakdown,
      suffix: null,
    };
  }

  // No revenue line and no spend → no signal at all
  if (spend == null && bookingsValueUsd === 0) {
    return {
      state: "no_signal",
      value: null,
      formula: "",
      breakdown: ["No commission charges, no fixed fee, no ad spend yet."],
      suffix: null,
    };
  }

  // Explicit zero rate (e.g. free pilot) AND no spend / bookings → pre-billing
  if (
    rateConfigured === 0 &&
    bookingsValueUsd === 0 &&
    (spend ?? 0) === 0
  ) {
    return {
      state: "pre_billing",
      value: null,
      formula: "Pre-billing",
      breakdown: [
        "Commission rate explicitly set to 0% (free pilot or waived plan).",
        "No bookings or ad spend in the last 30 days.",
      ],
      suffix: null,
    };
  }

  // No rate set, no bookings, no real revenue — pre-billing
  if (
    rateConfigured == null &&
    bookingsValueUsd === 0 &&
    (spend ?? 0) === 0
  ) {
    return {
      state: "pre_billing",
      value: null,
      formula: "Pre-billing",
      breakdown: [
        "Customer not yet wired up for billing.",
        "No commission rate, no fixed fee, no commission charges, no ad spend yet.",
      ],
      suffix: null,
    };
  }

  // Path 2 — estimated. Use configured rate or default.
  const isExplicitZero = rateConfigured === 0;
  const effectiveRate = isExplicitZero
    ? 0
    : (rateConfigured ?? PROFIT_DEFAULT_RATE);
  const rateIsDefault = rateConfigured == null && !isExplicitZero;
  const expectedRevenue = bookingsValueUsd * effectiveRate;
  const value = expectedRevenue - (spend ?? 0);

  const ratePct = `${(effectiveRate * 100).toFixed(effectiveRate < 0.01 ? 2 : 0)}%`;
  const rateLabel = rateIsDefault
    ? `${ratePct} (default — customer's rate not yet configured)`
    : isExplicitZero
      ? `${ratePct} (explicitly set — free pilot / waived plan)`
      : ratePct;

  const breakdown = [
    `Booking value (30d): ${fmtUsd2(bookingsValueUsd)}`,
    `Commission rate: ${rateLabel}`,
    `Expected commission: ${fmtUsd2(bookingsValueUsd)} × ${ratePct} = ${fmtUsd2(expectedRevenue)}`,
    `Ad spend (30d): ${spend != null ? fmtUsd2(spend) : fmtUsd2(0)}`,
    `Profit = ${fmtUsd2(expectedRevenue)} − ${fmtUsd2(spend ?? 0)} = ${fmtUsd2(value)}`,
    "",
    rateIsDefault
      ? "Estimated — customer hasn't been billed yet, default rate applied."
      : "Estimated — customer hasn't been billed for this cycle yet.",
  ];

  return {
    state: "estimated",
    value,
    formula: "Booking Value × Rate − Ad Spend",
    breakdown,
    suffix: "(est)",
  };
}

/** Cell renderer — formats the number with the right color, suffix, and
 *  hover tooltip explaining the calculation. */
function ProfitCell({ info }: { info: ProfitInfo }) {
  const tooltipBody = (
    <div className="text-[12px] leading-snug max-w-[320px] py-1 text-content-primary">
      {info.formula && (
        <div className="font-semibold mb-1">{info.formula}</div>
      )}
      {info.breakdown.map((line, i) => (
        <div key={i} className={line === "" ? "h-1" : "tabular-nums"}>
          {line}
        </div>
      ))}
    </div>
  );

  if (info.state === "no_signal") {
    return <span className="text-sm text-content-tertiary">—</span>;
  }

  if (info.state === "pre_billing") {
    return (
      <Tippy
        content={tooltipBody}
        theme="autumn-light"
        placement="top"
        delay={[150, 0]}
        duration={[120, 80]}
        arrow={true}
      >
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border bg-surface-secondary text-content-secondary border-border-primary cursor-help">
          Pre-billing
        </span>
      </Tippy>
    );
  }

  // real or estimated — render the number
  const v = info.value ?? 0;
  const cls =
    v > 0
      ? "text-brand-primary"
      : v < 0
        ? "text-brand-secondary"
        : "text-content-tertiary";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);

  return (
    <Tippy
      content={tooltipBody}
      theme="autumn-light"
      placement="top"
      delay={[150, 0]}
      duration={[120, 80]}
      arrow={true}
    >
      <span
        className={clsx(
          "inline-flex items-baseline gap-1 cursor-help",
          "text-sm font-medium tabular-nums",
          cls,
        )}
      >
        <span>
          {sign}
          {fmtUsd0(abs)}
        </span>
        {info.suffix && (
          <span className="text-[10px] text-content-tertiary font-normal">
            {info.suffix}
          </span>
        )}
      </span>
    </Tippy>
  );
}

/**
 * Derive a 1-5 word economic plan summary from contract + billing data.
 * Examples:
 *   "Commission 13%, M2M"
 *   "Commission 13% or $329 min, M2M"   (commission_or_flat)
 *   "Fixed $99/mo, 12mo"
 *   "Commission 5% + $50/mo, M2M"
 *   "Commission TBD, M2M"     (marketing customer, rate not yet set)
 *   "All-in-One TBD"          (all-in-one customer, rate + fee not yet set)
 *
 * Drops the Stripe plan name entirely — "Autumn Flagship" / "Free trial"
 * don't tell the CSM what the customer pays. Pulled deterministically
 * from existing fields. No "Free trial" output: marketing customers are
 * commission-based by definition even when contract.commissionRate is
 * null (no contract row yet) — surface "Commission TBD" instead.
 */
function formatEconomicPlan(row: CustomerRow): string {
  const b = row.billing;
  const c = row.contract;
  const ratePct =
    b.commissionRate != null ? Math.round(b.commissionRate * 100) : null;
  const fixed = b.fixedFeeMonthly ?? 0;
  const hasFixed = fixed > 0;
  const hasCommission = ratePct != null && ratePct > 0;

  // Contractual floor: "commission OR the minimum, whichever is greater".
  // Rendered as "or $X min" rather than "+ $X" so it can't be misread as a
  // platform fee charged on top of the commission — which is what "+" means
  // in the hybrid case immediately below.
  const minimumFee =
    b.billingType === "commission_or_flat" && (b.minimumFeeAmount ?? 0) > 0
      ? b.minimumFeeAmount!
      : null;

  const parts: string[] = [];
  if (minimumFee != null) {
    parts.push(
      hasCommission
        ? `Commission ${ratePct}% or $${minimumFee.toLocaleString()} min`
        : `Commission TBD or $${minimumFee.toLocaleString()} min`,
    );
  } else if (hasCommission && hasFixed) {
    parts.push(`Commission ${ratePct}% + $${fixed}/mo`);
  } else if (hasCommission) {
    parts.push(`Commission ${ratePct}%`);
  } else if (hasFixed) {
    parts.push(`Fixed $${fixed}/mo`);
  } else if (row.productType === "marketing") {
    // Marketing-only customers are commission-based by definition. The
    // rate may not be in the customer_contract row yet (CSM hasn't filled
    // it in) — surface "Commission TBD" so it's clear this is a real
    // paying customer, not a trial.
    parts.push("Commission TBD");
  } else if (row.productType === "all_in_one") {
    // All-in-One customers run on commission + optional fixed fee.
    parts.push("All-in-One TBD");
  } else {
    parts.push("No active plan");
  }

  // Term suffix — M2M when no end date is set, otherwise N-mo term.
  if (c.termMonths != null && c.termMonths > 0) {
    parts.push(`${c.termMonths}mo`);
  } else {
    parts.push("M2M");
  }
  return parts.join(", ");
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

const PRODUCT_BADGE: Record<string, string> = {
  marketing: "bg-brand-primary/10 text-brand-primary",
  all_in_one: "bg-brand-secondary/10 text-brand-secondary",
};

export const PRODUCT_LABEL: Record<string, string> = {
  marketing: "Marketing",
  all_in_one: "All-in-One",
};

const CHARGE_STATUS_BADGE: Record<string, string> = {
  paid: "bg-brand-primary/10 text-brand-primary",
  failed: "bg-brand-secondary/10 text-brand-secondary",
  pending: "bg-yellow-100 text-yellow-700",
};

// Column-spec-revised badge dictionaries

const CHURN_RISK_BADGE: Record<string, string> = {
  green: "bg-brand-primary/10 text-brand-primary",
  yellow: "bg-yellow-100 text-yellow-700",
  red: "bg-brand-secondary/10 text-brand-secondary",
};

export const PMS_LABEL: Record<string, string> = {
  mews: "Mews",
  cloudbeds: "Cloudbeds",
  resnexus: "ResNexus",
  opera: "Opera",
  thinkreservations: "ThinkReservations",
  innroad: "InnRoad",
  rezstream: "RezStream",
  innquest: "InnQuest",
  hostaway: "Hostaway",
  iqware: "IQware",
  lodgify: "Lodgify",
  webrezpro: "WebRezPro",
  other: "Other",
};

function ProductBadge({ type }: { type: string }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap",
        PRODUCT_BADGE[type] ?? "bg-gray-100 text-gray-500",
      )}
    >
      {PRODUCT_LABEL[type] ?? type}
    </span>
  );
}


function ChargeStatusBadge({ status }: { status: string | null }) {
  if (!status)
    return <span className="text-content-tertiary text-sm italic">—</span>;
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap",
        CHARGE_STATUS_BADGE[status] ?? "bg-gray-100 text-gray-500",
      )}
    >
      {status}
    </span>
  );
}

/**
 * Optimistic-update helper — patches the cached customer-list query so the
 * row reflects the change immediately without waiting for a refetch.
 */
function useOptimisticRowUpdate() {
  const queryClient = useQueryClient();
  return (id: string, patch: (row: CustomerRow) => CustomerRow) => {
    queryClient.setQueryData<CustomerListResponse>(
      CUSTOMER_LIST_QUERY_KEY,
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) => (r.id === id ? patch(r) : r)),
        };
      },
    );
  };
}

function TestFlagCell({ row }: { row: CustomerRow }) {
  const optimisticUpdate = useOptimisticRowUpdate();
  const [pending, setPending] = useState(false);
  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    optimisticUpdate(row.id, (r) => ({ ...r, isTestAccount: next }));
    setPending(true);
    try {
      await updateTestFlag(row.id, next);
    } catch {
      // Roll back on failure.
      optimisticUpdate(row.id, (r) => ({ ...r, isTestAccount: !next }));
    } finally {
      setPending(false);
    }
  };
  return (
    <input
      type="checkbox"
      checked={row.isTestAccount}
      onChange={onChange}
      disabled={pending}
      onClick={(e) => e.stopPropagation()}
      className="h-4 w-4 rounded border-border-primary accent-brand-primary focus:ring-brand-primary cursor-pointer"
      aria-label="Test account"
    />
  );
}

function CarrCell({ row }: { row: CustomerRow }) {
  const optimisticUpdate = useOptimisticRowUpdate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    row.billing.carr != null ? String(row.billing.carr) : "",
  );
  // Guard against the Escape→blur race: pressing Escape resets state, but
  // the input's blur handler still fires synchronously and would otherwise
  // commit the typed draft. The ref flips to true in the Escape branch so
  // commit() can short-circuit without touching the server.
  const cancelledRef = useRef(false);
  const carr = row.billing.carr;
  const isPlaceholder = row.billing.carrIsPlaceholder;

  const commit = async () => {
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const trimmed = draft.trim();
    const nextDollars = trimmed === "" ? null : parseFloat(trimmed);
    if (nextDollars != null && (Number.isNaN(nextDollars) || nextDollars < 0)) {
      setDraft(carr != null ? String(carr) : "");
      return;
    }
    const carrCents = nextDollars != null ? Math.round(nextDollars * 100) : null;
    optimisticUpdate(row.id, (r) => {
      const manual = nextDollars != null;
      return {
        ...r,
        billing: {
          ...r.billing,
          carr: nextDollars,
          carrIsManual: manual,
          carrIsPlaceholder: manual ? false : r.billing.carrIsPlaceholder,
          // Mirror backend: a manual CARR drives ACV/ARR (= CARR) and MRR
          // (= CARR/12) so the three columns stay in sync immediately.
          acvArrDefault: manual ? nextDollars : r.billing.acvArrDefault,
          monthlyRevenue: manual ? nextDollars / 12 : r.billing.monthlyRevenue,
        },
      };
    });
    try {
      await updateCarr(row.id, carrCents);
    } catch {
      // Roll back on failure — leave the user's draft so they can retry.
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        value={draft}
        min={0}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            cancelledRef.current = true;
            setDraft(carr != null ? String(carr) : "");
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="w-24 text-right text-sm border border-border-primary rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-primary"
      />
    );
  }

  // Wrap the placeholder value in a soft tooltip explaining the default,
  // but no inline "est." chip per directive 2026-04-26.
  const buttonInner = (
    <span
      className={clsx(
        "text-sm font-medium tabular-nums",
        isPlaceholder
          ? "text-content-tertiary hover:text-brand-primary"
          : "text-content-primary hover:text-brand-primary",
      )}
    >
      {formatCurrency(carr)}
    </span>
  );

  const button = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setDraft(carr != null ? String(carr) : "");
        setEditing(true);
      }}
      className="cursor-pointer"
    >
      {buttonInner}
    </button>
  );

  if (isPlaceholder) {
    return (
      <Tippy
        theme="autumn-light"
        content={
          <span className="text-xs">
            Default — no revenue data yet, using $6,000 placeholder. Click to set a real value.
          </span>
        }
        delay={[150, 0]}
      >
        {button}
      </Tippy>
    );
  }
  return button;
}

function LastChargeCell({ row }: { row: CustomerRow }) {
  const status = row.billing.latestChargeStatus;
  const chargedAt = row.billing.latestChargedAt;
  const amount = row.billing.lastCommissionBill;

  if (!status && !chargedAt && amount == null) {
    return <span className="text-sm text-content-tertiary">—</span>;
  }

  const isPaid = status === "paid";
  const cls = isPaid
    ? "text-brand-primary font-medium"
    : status === "failed"
      ? "text-brand-secondary font-medium"
      : "text-content-secondary";
  const statusLabel = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : "Unknown";
  const tooltipBody = (
    <div className="text-xs leading-snug">
      <div className="font-medium text-content-primary">{statusLabel}</div>
      {chargedAt && (
        <div className="text-content-secondary">{formatDate(chargedAt)}</div>
      )}
    </div>
  );

  return (
    <Tippy theme="autumn-light" content={tooltipBody} delay={[150, 0]}>
      <span
        className={clsx(
          "text-sm tabular-nums whitespace-nowrap cursor-help",
          cls,
        )}
      >
        {formatCurrency(amount)}
      </span>
    </Tippy>
  );
}

function ProductCapsulesCell({ row }: { row: CustomerRow }) {
  const optimisticUpdate = useOptimisticRowUpdate();
  const [open, setOpen] = useState(false);
  const ALL: ProductCapsule[] = ["sem", "website", "email", "social", "pricing"];
  const current = row.productCapsules ?? [];

  const toggle = async (capsule: ProductCapsule) => {
    // Capture the prior state so we can roll back the WHOLE optimistic
    // patch (capsules + source + timestamp) on failure, not just the
    // capsules. Without this, a rejected mutation leaves the row showing
    // "Manual · today" indefinitely even though the server didn't accept
    // the change.
    const prevCapsules = row.productCapsules ?? [];
    const prevSource = row.productCapsulesSource;
    const prevUpdatedAt = row.productCapsulesUpdatedAt;

    // Read-then-write via the functional update so two rapid toggles
    // compose correctly (each one toggles relative to the freshest cache
    // state, not the value `current` was closed over at click time).
    let nextForRequest: ProductCapsule[] = [];
    optimisticUpdate(row.id, (r) => {
      const live = r.productCapsules ?? [];
      const next = live.includes(capsule)
        ? live.filter((c) => c !== capsule)
        : [...live, capsule];
      nextForRequest = next;
      return {
        ...r,
        productCapsules: next,
        productCapsulesSource: "manual",
        productCapsulesUpdatedAt: new Date().toISOString(),
      };
    });
    try {
      await updateProductCapsules(row.id, nextForRequest);
    } catch {
      optimisticUpdate(row.id, (r) => ({
        ...r,
        productCapsules: prevCapsules,
        productCapsulesSource: prevSource,
        productCapsulesUpdatedAt: prevUpdatedAt,
      }));
    }
  };

  const sourceLabel =
    row.productCapsulesSource === "manual"
      ? "Manual"
      : row.productCapsulesSource === "ai"
        ? "AI inferred"
        : "Not yet inferred";
  const updatedLabel = row.productCapsulesUpdatedAt
    ? formatDate(row.productCapsulesUpdatedAt)
    : null;

  const tooltipBody = (
    <div className="text-xs leading-snug min-w-[180px]">
      <div className="font-medium text-content-primary mb-1">
        Products sold
      </div>
      <div className="space-y-1">
        {ALL.map((c) => (
          <label
            key={c}
            className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5"
            onClick={(e) => {
              e.stopPropagation();
              toggle(c);
            }}
          >
            <input
              type="checkbox"
              checked={current.includes(c)}
              readOnly
              className="h-3 w-3 rounded border-border-primary accent-brand-primary"
            />
            <span className="text-content-primary">
              {PRODUCT_CAPSULE_LABELS[c]}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-border-primary text-content-tertiary">
        Source: {sourceLabel}
        {updatedLabel && ` · ${updatedLabel}`}
      </div>
    </div>
  );

  return (
    <Tippy
      theme="autumn-light"
      content={tooltipBody}
      interactive
      visible={open}
      onClickOutside={() => setOpen(false)}
      delay={[0, 0]}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex flex-wrap gap-1 max-w-[220px] cursor-pointer"
      >
        {current.length === 0 ? (
          <span className="text-xs text-content-tertiary italic">
            (click to set)
          </span>
        ) : (
          current.map((c) => (
            <span
              key={c}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary/20"
            >
              {PRODUCT_CAPSULE_LABELS[c]}
            </span>
          ))
        )}
      </button>
    </Tippy>
  );
}

function ChurnRiskBadge({
  level,
  isManual,
}: {
  level: "green" | "yellow" | "red";
  isManual: boolean;
}) {
  if (level === "green") {
    // Manually-flagged-green still gets a subtle indicator so a CSM can
    // see their override is live (rather than the dash that means "no
    // signal computed"). Hover tooltip explains it's a manual override.
    if (isManual) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-brand-primary"
          title="Manually flagged green by CSM"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
          <span className="opacity-60">★</span>
        </span>
      );
    }
    return <span className="text-sm text-content-tertiary">—</span>;
  }
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
        CHURN_RISK_BADGE[level],
      )}
      title={isManual ? "Manually flagged" : "Auto-detected"}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {level === "red" ? "Red" : "Yellow"}
      {isManual && <span className="opacity-60">★</span>}
    </span>
  );
}

function OwnerStack({
  owners,
}: {
  owners: { adminEmail: string; adminFirstName: string | null }[];
}) {
  if (!owners?.length) {
    return <span className="text-sm text-content-tertiary">Unassigned</span>;
  }
  return (
    <div className="flex items-center -space-x-1.5">
      {owners.slice(0, 3).map((o, i) => {
        const initial =
          (o.adminFirstName?.[0] ?? o.adminEmail[0] ?? "?").toUpperCase();
        return (
          <span
            key={i}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-semibold ring-2 ring-white"
            title={o.adminEmail}
          >
            {initial}
          </span>
        );
      })}
      {owners.length > 3 && (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-content-secondary text-[10px] font-medium ring-2 ring-white">
          +{owners.length - 3}
        </span>
      )}
    </div>
  );
}

function MarginTrendIndicator({
  trend,
}: {
  trend: { slope: number; dataPoints: number[] } | null;
}) {
  if (!trend || trend.dataPoints.length < 2) {
    return <span className="text-sm text-content-tertiary">—</span>;
  }
  const { slope } = trend;
  const flat = Math.abs(slope) < 1;
  if (flat) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-content-tertiary">
        → flat
      </span>
    );
  }
  const rising = slope > 0;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 text-sm font-medium",
        rising ? "text-brand-primary" : "text-brand-secondary",
      )}
      title={`Slope: ${slope.toFixed(2)} / day`}
    >
      {rising ? "↑" : "↓"}
      {rising ? "rising" : "falling"}
    </span>
  );
}

function NotePreview({
  note,
}: {
  note: {
    body: string;
    createdAt: string;
    totalCount: number;
  } | null;
}) {
  if (!note) {
    return <span className="text-sm text-content-tertiary italic">—</span>;
  }
  const preview =
    note.body.length > 48 ? note.body.slice(0, 48).trim() + "…" : note.body;
  return (
    <span
      className="text-sm text-content-primary"
      title={`${note.body}\n\n${note.totalCount} note(s) total · ${formatDate(note.createdAt)}`}
    >
      {preview}
      {note.totalCount > 1 && (
        <span className="ml-1 text-xs text-content-tertiary">
          ({note.totalCount})
        </span>
      )}
    </span>
  );
}

function NextActionPreview({
  body,
  dueDate,
}: {
  body: string | null;
  dueDate: string | null;
}) {
  if (!body) {
    return <span className="text-sm text-content-tertiary italic">—</span>;
  }
  const overdue = dueDate && new Date(dueDate).getTime() < Date.now();
  return (
    <div className="flex flex-col gap-0.5 leading-tight max-w-[220px]">
      <span className="text-sm text-content-primary truncate" title={body}>
        {body}
      </span>
      {dueDate && (
        <span
          className={clsx(
            "text-[11px] whitespace-nowrap",
            overdue ? "text-brand-secondary font-medium" : "text-content-tertiary",
          )}
        >
          {overdue ? "Overdue · " : "Due "}
          {formatDate(dueDate)}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier Hover Popover
// ---------------------------------------------------------------------------

export function TierPopover({
  tier,
  services,
  commissionRate,
  termMonths,
  signedDate,
  cancellationClause,
}: {
  tier: string | null;
  services: string | null;
  commissionRate: number | null;
  termMonths: number | null;
  signedDate: string | null;
  cancellationClause: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const hasDetails =
    services || commissionRate != null || termMonths != null || signedDate || cancellationClause;

  const handleMouseEnter = () => {
    if (!hasDetails) return;
    hoverTimeout.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top - 8,
          left: rect.left + rect.width / 2,
        });
      }
      setIsOpen(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsOpen(false);
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        ref={triggerRef}
        className={clsx(
          "text-sm text-content-primary",
          hasDetails && "underline decoration-dotted cursor-default",
        )}
      >
        {tier ?? "—"}
      </span>

      {isOpen && hasDetails && (
        <div
          className="fixed z-[9999] bg-surface-primary rounded-lg shadow-xl border border-border-secondary min-w-[240px] overflow-hidden"
          style={{
            top: position.top,
            left: position.left,
            transform: "translate(-50%, -100%)",
          }}
        >
          {/* Arrow pointing down */}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white"
            style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.1))" }}
          />
          <div className="px-3 py-2 border-b border-border-secondary bg-surface-secondary">
            <h4 className="text-xs font-semibold text-content-primary">
              Contract Details
            </h4>
          </div>
          <div className="p-3 space-y-1.5">
            {services && (
              <Row label="Services" value={services} />
            )}
            {commissionRate != null && (
              <Row label="Commission rate" value={formatPercent(commissionRate)} />
            )}
            {termMonths != null && (
              <Row label="Term" value={`${termMonths} months`} />
            )}
            {signedDate && (
              <Row label="Signed" value={formatDate(signedDate)} />
            )}
            {cancellationClause && (
              <Row label="Cancellation" value={cancellationClause} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 text-xs">
      <span className="text-content-tertiary shrink-0">{label}</span>
      <span className="text-content-primary text-right">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const col = createColumnHelper<CustomerRow>();

/**
 * Extra context handed to the table via `useReactTable({ meta })` and read in
 * cells through `info.table.options.meta`. `acvMode` drives the ARR column;
 * the selection helpers power the bulk "mark as test" action that
 * CustomerList renders. All optional so the columns still render if a future
 * consumer mounts the table without selection wiring.
 */
export interface CrmTableMeta {
  acvMode?: "default" | "avg3";
  selectedIds?: Set<string>;
  toggleSelect?: (id: string) => void;
  toggleSelectAll?: () => void;
  /** Ids of the currently-filtered rows - drives the select-all header state. */
  selectableIds?: string[];
}

export const crmColumns = [
  // ---- Group 0: Counter + identity ----
  col.display({
    id: "rowNumber",
    header: Object.assign(
      (ctx: HeaderContext<CustomerRow, unknown>) => {
        const meta = ctx.table.options.meta as CrmTableMeta | undefined;
        if (!meta?.toggleSelectAll) {
          return (
            <span className="text-content-tertiary text-xs font-medium">#</span>
          );
        }
        const ids = meta.selectableIds ?? [];
        const sel = meta.selectedIds;
        const allSelected =
          ids.length > 0 && sel != null && ids.every((id) => sel.has(id));
        const someSelected = sel != null && ids.some((id) => sel.has(id));
        return (
          <span className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              aria-label="Select all rows"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected && someSelected;
              }}
              onChange={() => meta.toggleSelectAll?.()}
              onClick={(e) => e.stopPropagation()}
              className="h-3.5 w-3.5 rounded border-border-primary accent-brand-primary focus:ring-brand-primary cursor-pointer align-middle"
            />
            <span className="text-content-tertiary text-xs font-medium">#</span>
          </span>
        );
      },
      { label: "#" },
    ),
    enableSorting: true,
    // Sort by createdAt so #1 is the oldest customer when ascending. The
    // visible cell text always re-numbers 1..N based on the current sort
    // (handled by getSortedRowModel().findIndex below).
    sortingFn: (a, b) => {
      const va = new Date(a.original.createdAt).getTime();
      const vb = new Date(b.original.createdAt).getTime();
      return va - vb;
    },
    cell: (info) => {
      const sortedRows = info.table.getSortedRowModel().rows;
      const idx = sortedRows.findIndex((r) => r.id === info.row.id);
      const meta = info.table.options.meta as CrmTableMeta | undefined;
      const id = info.row.original.id;
      // No selection wiring → plain row number (preserves prior behavior).
      if (!meta?.toggleSelect) {
        return (
          <span className="text-xs text-content-tertiary tabular-nums">
            {idx + 1}
          </span>
        );
      }
      const selected = meta.selectedIds?.has(id) ?? false;
      // Number by default; checkbox appears on row hover (the <tr> carries
      // `group`) or keyboard focus, and stays shown once selected. The
      // checkbox keeps layout (opacity, not display:none) so it stays in the
      // tab order and tappable on touch devices; the row number is overlaid
      // absolutely with pointer-events-none so clicks land on the checkbox.
      return (
        <span className="relative inline-flex items-center">
          <input
            type="checkbox"
            aria-label="Select row"
            checked={selected}
            onChange={() => meta.toggleSelect?.(id)}
            onClick={(e) => e.stopPropagation()}
            className={clsx(
              "peer h-3.5 w-3.5 rounded border-border-primary accent-brand-primary focus:ring-brand-primary cursor-pointer",
              !selected &&
                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            )}
          />
          {!selected && (
            <span className="absolute inset-0 flex items-center text-xs text-content-tertiary tabular-nums pointer-events-none group-hover:opacity-0 peer-focus-visible:opacity-0">
              {idx + 1}
            </span>
          )}
        </span>
      );
    },
  }),

  col.accessor("hotelName", {
    id: "hotelName",
    header: headerTip(
      "Hotel",
      "The hotel's name. Falls back to the customer's primary email if no hotel name was set during onboarding.",
    ),
    enableSorting: true,
    cell: (info) => {
      const row = info.row.original;
      return (
        <Link
          to={`/crm/customers/${row.id}`}
          className="flex flex-col gap-0.5 leading-tight"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="font-medium text-content-primary hover:text-brand-primary hover:underline whitespace-nowrap">
            {row.hotelName ?? row.primaryEmail}
          </span>
          {/* Always render the email below — it's the canonical username
              and CSMs need it visible at all times. The conditional was
              hiding it whenever hotelName fell back to the email itself,
              but now the backend always returns a derived hotelName so
              the email never doubles up here. */}
          {row.primaryEmail && row.primaryEmail !== row.hotelName && (
            <span className="text-xs text-content-tertiary whitespace-nowrap">
              {row.primaryEmail}
            </span>
          )}
        </Link>
      );
    },
  }),

  col.accessor("productType", {
    id: "productType",
    header: headerTip(
      "Type",
      "Cohort the customer belongs to: 'Marketing' = ads + analytics only, 'All-in-One' = ads plus PMS-connected pricing. The Product column shows the actual products sold.",
    ),
    enableSorting: true,
    cell: (info) => <ProductBadge type={info.getValue()} />,
  }),

  col.accessor("isTestAccount", {
    id: "testFlag",
    header: headerTip(
      "Test",
      "Mark this customer as a test/QA account. Test accounts are hidden by default in the table, excluded from the portfolio totals at the top, and — since ADR 0041 — dropped from Tracking Health on the next daily sync: no rules, no Validator, no alerts. Nothing there is thrown away, and un-marking brings the hotel back the next morning.",
    ),
    enableSorting: true,
    cell: (info) => <TestFlagCell row={info.row.original} />,
  }),

  col.accessor("billing.carr", {
    id: "carr",
    header: headerTip(
      "CARR",
      "Contracted ARR. Click to override per row. Customers <2 months tenured default to $6,000 ($500 MRR × 12) since revenue history is too thin to project; tenured customers default to ACV/ARR.",
    ),
    enableSorting: true,
    cell: (info) => <CarrCell row={info.row.original} />,
  }),

  col.accessor((row) => row.billing.lastCommissionBill ?? -Infinity, {
    id: "lastCharge",
    header: headerTip(
      "Last Charge",
      "Amount of the most recent commission invoice. Green = paid, red = failed. Hover for status + date.",
    ),
    enableSorting: true,
    sortingFn: "basic",
    cell: (info) => <LastChargeCell row={info.row.original} />,
  }),

  col.accessor("createdAt", {
    id: "createdAt",
    header: headerTip(
      "Created",
      "When the customer's account was first created in Autumn.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {formatDate(info.getValue())}
      </span>
    ),
  }),

  // ---- Group 2: Contract + Plan ----
  // `tier`, `services`, `termMonths` removed per user direction 2026-04-22 —
  // those fields didn't map to Stripe and were noise. Plan comes from Stripe
  // product.name + product.description live-synced into UserSubscriptionCache.
  // Plan = a 1-5 word economic summary derived from contract + billing
  // (commission %, fixed fee, term length). Replaces the Stripe plan
  // name (e.g. "Autumn Flagship") which didn't convey the deal economics.
  // Per directive 2026-04-26: term · free trial · fixed amount · commission.
  col.accessor((row) => formatEconomicPlan(row), {
    id: "plan",
    header: headerTip(
      "Plan",
      "Short economic summary of the deal: commission rate, fixed fee, term length. Derived from contract + billing data, not the Stripe plan name. Hover for the full Stripe plan name as context.",
    ),
    enableSorting: true,
    sortingFn: "alphanumeric",
    cell: (info) => {
      const b = info.row.original.billing;
      const summary = info.getValue() as string;
      return (
        <Tippy
          theme="autumn-light"
          content={
            <div className="text-xs leading-snug max-w-[260px]">
              <div className="font-medium text-content-primary mb-0.5">
                Stripe plan
              </div>
              <div className="text-content-secondary">
                {b.planName ?? "(no Stripe subscription)"}
              </div>
              {b.planDescription && (
                <div className="text-content-tertiary mt-1">
                  {b.planDescription}
                </div>
              )}
            </div>
          }
          delay={[150, 0]}
        >
          <span className="text-sm font-medium text-content-primary cursor-help">
            {summary}
          </span>
        </Tippy>
      );
    },
  }),

  col.display({
    id: "products",
    header: headerTip(
      "Product",
      "Which Autumn products this customer is sold (SEM, Website, Email, Social, Pricing). Click to edit per row. Defaults come from a nightly AI inference; manual edits stay sticky.",
    ),
    enableSorting: true,
    // Sort by capsule count — most-products-sold first when descending.
    sortingFn: (a, b) =>
      (a.original.productCapsules?.length ?? 0) -
      (b.original.productCapsules?.length ?? 0),
    cell: (info) => <ProductCapsulesCell row={info.row.original} />,
  }),

  col.accessor("billing.commissionRate", {
    id: "commissionRate",
    header: headerTip(
      "Commission %",
      "The commission rate Autumn charges on this customer's bookings revenue. Editable from the /billing page.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary whitespace-nowrap">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("contract.signedDate", {
    id: "signedDate",
    header: headerTip(
      "Signed",
      "The date the customer signed their contract. Currently empty for most customers — will populate once the contract backfill runs.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {formatDate(info.getValue())}
      </span>
    ),
  }),

  // ---- Group 3: Billing ----
  col.accessor("billing.totalCommissionYtd", {
    id: "totalCommissionYtd",
    header: headerTip(
      "Commission YTD",
      "Total commission Autumn has collected from this customer year-to-date — sum of all paid commission invoices in the current calendar year.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary whitespace-nowrap font-medium">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  // latestChargeStatus + latestChargedAt are now combined into the
  // `lastCharge` column up in the Primary section.

  col.accessor("billing.chargeCountAllTime", {
    id: "chargeCountAllTime",
    header: headerTip(
      "Charge Count",
      "How many commission invoices this customer has had over their lifetime — indicates how many billing cycles are complete.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">{info.getValue()}</span>
    ),
  }),

  // ---- Group 3.5: Phase 2 billing — real values from UserSubscriptionCache + commission_charge ----
  col.accessor("billing.lastCommissionBill", {
    id: "lastCommissionBill",
    header: headerTip(
      "Last Commission",
      "Dollar amount of the most recent paid commission invoice. Used as the 'latest month' input when ARR is in 'Latest month' projection mode.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  col.accessor("billing.fixedFeeMonthly", {
    id: "fixedFeeMonthly",
    header: headerTip(
      "Fixed Fee/mo",
      "The customer's monthly subscription fee from Stripe. $0 means a commission-only plan (waived fixed fee).",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null || Number(v) === 0) {
        return <span className="text-sm text-content-tertiary">—</span>;
      }
      return (
        <span className="text-sm text-content-primary">{formatCurrency(v)}</span>
      );
    },
  }),

  col.accessor("billing.monthlyRevenue", {
    id: "monthlyRevenue",
    header: headerTip(
      "MRR",
      "Monthly recurring revenue from this customer: most recent commission invoice + their monthly fixed fee.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm font-medium text-content-primary">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  col.accessor("billing.acvArrDefault", {
    id: "acvArr",
    header: headerTip(
      "ACV/ARR",
      "Annual run-rate revenue. Use the toggle above the table to switch views:\n• Latest month: last month's commission + fixed fee, × 12 — reactive, swings month-to-month.\n• Trailing 3-mo avg: average of last 3 months + fixed fee, × 12 — smoother, what investors typically see.",
    ),
    enableSorting: true,
    cell: (info) => {
      // Toggle-sensitive — when acvMode = 'avg3', read acvArrAvg3 instead.
      // tableMeta is passed through via the table instance below.
      const mode =
        (info.table.options.meta as { acvMode?: "default" | "avg3" } | undefined)
          ?.acvMode ?? "default";
      const v =
        mode === "avg3"
          ? info.row.original.billing.acvArrAvg3
          : info.row.original.billing.acvArrDefault;
      return (
        <span className="text-sm font-medium text-content-primary">
          {formatCurrency(v)}
        </span>
      );
    },
  }),

  col.accessor("billing.paymentMethodStatus", {
    id: "paymentMethodStatus",
    header: headerTip(
      "Card Status",
      "Combined billing health for the customer's default card.\n• OK — card on file, no issues\n• No Card — no default card set in Stripe\n• Charge declined — most recent charge failed (even if the card itself is valid)\n• Card expired / Expiring soon — card needs an update",
    ),
    enableSorting: true,
    cell: (info) => {
      const b = info.row.original.billing;
      const view = deriveCardStatus({
        paymentMethodStatus: b.paymentMethodStatus,
        latestChargeStatus: b.latestChargeStatus,
        failedChargeCount: b.failedChargeCount,
      });
      if (!view) {
        return <span className="text-xs text-gray-400">—</span>;
      }
      const cls =
        view.tone === "ok"
          ? "bg-brand-primary/10 text-brand-primary"
          : view.tone === "warn"
            ? "bg-yellow-100 text-yellow-700"
            : "bg-brand-secondary/10 text-brand-secondary";
      return (
        <span
          title={view.tooltip}
          className={clsx(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-transparent",
            cls,
          )}
        >
          {view.label}
        </span>
      );
    },
  }),

  // ---- Group 4: Activity ----
  col.accessor("activity.lastActive", {
    id: "lastActive",
    header: headerTip(
      "Last Log In",
      "When this customer last appeared anywhere in the system — any inbound or outbound email, ticket, note, feedback, or billing event. (Will be replaced with real login tracking in a follow-up.)",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {relativeTime(info.getValue())}
      </span>
    ),
  }),

  col.accessor("activity.daysSinceLastEmail", {
    id: "daysSinceLastEmail",
    header: headerTip(
      "Days Since Email",
      "Days since the last email exchange with this customer — inbound or outbound, combined.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {info.getValue() ?? "—"}
      </span>
    ),
  }),

  col.accessor("activity.dashboardUnlockedAt", {
    id: "dashboardUnlockedAt",
    header: headerTip(
      "Dashboard Unlocked",
      "When the customer's dashboard was unlocked — typically right after their Google Ads campaigns went live. Falls back to their account creation date for older customers.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {formatDate(info.getValue())}
      </span>
    ),
  }),

  col.accessor("activity.shouldPushPricesEnabledAt", {
    id: "pricingGoLive",
    header: headerTip(
      "Pricing Go-Live",
      "When the customer turned on Autumn's pricing engine and Autumn started pushing prices to their PMS. Empty for marketing-only customers (they don't push prices).",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {formatDate(info.getValue())}
      </span>
    ),
  }),

  // ---- Group 5: Workload ----
  col.accessor("workload.openTicketCount", {
    id: "openTicketCount",
    header: headerTip(
      "Open Tickets",
      "How many CS tickets are still open for this customer (not yet resolved, replied, or closed).",
    ),
    enableSorting: true,
    cell: (info) => {
      const count = info.getValue();
      return count > 0 ? (
        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-semibold bg-brand-secondary/10 text-brand-secondary">
          {count}
        </span>
      ) : (
        <span className="text-sm text-content-tertiary">0</span>
      );
    },
  }),

  col.accessor("workload.totalTicketCount", {
    id: "totalTicketCount",
    header: headerTip(
      "Total Tickets",
      "Lifetime count of CS tickets ever raised by this customer — open + resolved + closed.",
    ),
    enableSorting: true,
    cell: (info) => {
      const count = info.getValue();
      return count > 0 ? (
        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-semibold bg-surface-secondary text-content-secondary border border-border-secondary">
          {count}
        </span>
      ) : (
        <span className="text-sm text-content-tertiary">0</span>
      );
    },
  }),

  col.accessor("workload.needsReplyCount", {
    id: "needsReplyCount",
    header: headerTip(
      "Needs Reply",
      "Number of email threads where the customer wrote in and we haven't replied yet. Counts unique threads, not individual messages.",
    ),
    enableSorting: true,
    cell: (info) => {
      const count = info.getValue();
      return count > 0 ? (
        <span className="inline-flex items-center gap-1 px-2 h-[22px] rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
          {count}
        </span>
      ) : (
        <span className="text-sm text-content-tertiary">—</span>
      );
    },
  }),

  col.accessor("workload.ticketsThisMonth", {
    id: "ticketsThisMonth",
    header: headerTip(
      "Tickets (mo)",
      "How many CS tickets this customer has opened so far this calendar month — including ones that are already resolved.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">{info.getValue()}</span>
    ),
  }),

  // ---- Group 6: Marketing perf (Phase 2 — from marketing_metrics_snapshot) ----
  col.accessor("marketing.bookings30d", {
    id: "bookings30d",
    header: headerTip(
      "Bookings (30d)",
      "Number of bookings driven by Autumn-managed Google Ads campaigns in the last 30 days.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  col.accessor("marketing.bookingsValueUsd30d", {
    id: "bookingsValueUsd30d",
    header: headerTip(
      "Bookings $ (30d)",
      "Total dollar value of bookings driven by Autumn ads in the last 30 days. This is the customer's gross commissionable booking revenue.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm font-medium text-content-primary">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.impressions30d", {
    id: "impressions30d",
    header: headerTip(
      "Impressions (30d)",
      "Total ad impressions for this customer's active Google Ads campaigns over the last 30 days.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  // accessorFn (not a deep-key string) so rows from an older API payload
  // that lacks the field resolve to null instead of undefined — avoids
  // TanStack's "deeply nested key returned undefined" console warning.
  col.accessor((row) => row.marketing.impressions7d ?? null, {
    id: "impressions7d",
    header: headerTip(
      "Impressions (7d)",
      "Total ad impressions for this customer's active Google Ads campaigns over the last 7 days.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  col.accessor((row) => row.marketing.impressionsWowDelta ?? null, {
    id: "impressionsWowDelta",
    header: headerTip(
      "Impressions WoW",
      "Change in impressions: last 7 days vs the 7 days before. Positive = gaining ad visibility, negative = losing it.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) {
        return <span className="text-sm text-content-tertiary">—</span>;
      }
      const n = Number(v);
      const cls =
        n > 0
          ? "text-brand-primary"
          : n < 0
            ? "text-brand-secondary"
            : "text-content-primary";
      const sign = n > 0 ? "+" : "";
      return (
        <span className={clsx("text-sm font-medium", cls)}>
          {sign}
          {Math.round(n * 100)}%
        </span>
      );
    },
  }),

  col.accessor("marketing.clicks30d", {
    id: "clicks30d",
    header: headerTip(
      "Clicks (30d)",
      "Total ad clicks for this customer's active Google Ads campaigns over the last 30 days.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  col.accessor("marketing.revenue30d", {
    id: "revenue30d",
    header: headerTip(
      "Revenue (30d)",
      "Gross commission revenue Autumn earns from this customer's last-30-day bookings — before subtracting ad spend.\nFormula: bookings $ × commission rate.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) {
        return <span className="text-sm text-content-tertiary">—</span>;
      }
      return (
        <span className="text-sm text-brand-primary font-medium tabular-nums">
          {formatCurrency(v)}
        </span>
      );
    },
  }),

  col.accessor("marketing.spend30d", {
    id: "spend30d",
    header: headerTip(
      "Ad Spend",
      "What Autumn paid Google for ads on this customer's behalf in the last 30 days.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  // Profit = (Autumn's revenue from this customer) − (ad spend on them).
  //
  // Three paths, in priority order:
  //   1. REAL — customer has lastCommissionBill > 0 OR fixedFeeMonthly > 0.
  //      Uses MRR (= lastCommBill + fixedFee). Same as the legacy formula.
  //   2. ESTIMATED — no actual billing yet, but customer has a configured
  //      commissionRate (or we can default-assume one) and either booking
  //      value or ad spend. Computes expected commission revenue
  //      (bookingsValueUsd × rate) so onboarding-stage customers don't
  //      sit blank for weeks before their first cycle. Uses the customer's
  //      configured rate when present; falls back to PROFIT_DEFAULT_RATE
  //      (13%) when rate is null (typical for not-yet-onboarded). Never
  //      overrides an explicit `commissionRate = 0` (free pilots etc.).
  //   3. PRE-BILLING — no spend AND no booking value AND no real revenue.
  //      Renders a "Pre-billing" badge instead of "—" so CSMs can tell
  //      "we haven't wired this customer yet" apart from "data missing".
  //
  // Tooltip on hover explains which path was taken and shows the inputs.
  // MRR / ARR placeholder logic ($500 / $6k) is intentional and stays —
  // this column reads underlying signals directly, not the placeholder.
  col.accessor(
    (row) => {
      const info = computeProfitInfo(row);
      return info.value ?? -Infinity;
    },
    {
    id: "margin30d",
    header: headerTip(
      "Profit",
      "Autumn's net profit on this customer over the last 30 days.\nFormula: Revenue − Ad Spend.\n• Real customers: actual MRR (commission + fixed fee) − spend.\n• Onboarding customers: estimated commission (booking value × rate) − spend, marked '(est)'.\n• Pre-billing customers: badge instead of number when nothing is wired yet.\nHover any cell for the exact calculation.",
    ),
    enableSorting: true,
    sortingFn: "basic",
    cell: (info) => {
      const profitInfo = computeProfitInfo(info.row.original);
      return <ProfitCell info={profitInfo} />;
    },
  }),

  col.accessor("marketing.autumnShareOfTraffic", {
    id: "autumnShareOfTraffic",
    header: headerTip(
      "Traffic Share",
      "Share of the hotel's engaged sessions (GA4 Paid Search) driven by Autumn ads since go-live. Matches the 'Website Traffic from Autumn' tile on the customer's marketing dashboard.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.autumnShareOfBookings", {
    id: "autumnShareOfBookings",
    header: headerTip(
      "Booking Share",
      "Share of the hotel's bookings that came from Autumn-managed ads (vs. direct, OTAs, etc.). Higher = Autumn drove more of their revenue.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.brandProtectionPct", {
    id: "brandProtectionPct",
    header: headerTip(
      "Brand Protection %",
      "Share of bookings driven by Brand Protection ads (Google Ads bidding on the hotel's own brand name). High % = customer is paying mostly for bookings they'd likely get anyway — campaign mix needs to shift toward growth.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.brandProtectionBookings30d", {
    id: "brandProtectionBookings30d",
    header: headerTip(
      "BP Bookings",
      "Raw count of bookings driven by Brand Protection ads in the last 30 days. Read alongside Brand Protection % to gauge magnitude — '1 of 1' is noise, '10 of 10' is real churn risk.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  col.accessor("marketing.nonBrandProtectionBookings30d", {
    id: "nonBrandProtectionBookings30d",
    header: headerTip(
      "Non-BP Bookings",
      "Raw count of bookings driven by non–Brand Protection ads (growth / discovery / competitor) in the last 30 days. The healthy growth signal — high here means Autumn is bringing in net-new bookings.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {v == null ? "—" : v.toLocaleString()}
        </span>
      );
    },
  }),

  col.accessor("marketing.bookingsTrendDelta", {
    id: "bookingsTrendDelta",
    header: headerTip(
      "Trend Δ",
      "Bookings trend month-over-month: positive = growing vs. the prior 30 days, negative = declining.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) {
        return <span className="text-sm text-content-tertiary">—</span>;
      }
      const n = Number(v);
      const cls =
        n > 0
          ? "text-brand-primary"
          : n < 0
            ? "text-brand-secondary"
            : "text-content-primary";
      const sign = n > 0 ? "+" : "";
      return (
        <span className={clsx("text-sm font-medium", cls)}>
          {sign}
          {Math.round(n * 100)}%
        </span>
      );
    },
  }),

  col.accessor("marketing.occupancyLift30d", {
    id: "occupancyLift30d",
    header: headerTip(
      "Occ Lift",
      "Change in occupancy over the last 30 days compared to the same 30-day window a year ago. Empty when we don't have data going back a year.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.adrChange30d", {
    id: "adrChange30d",
    header: headerTip(
      "ADR Δ",
      "Change in average daily rate (ADR) over the last 30 days vs. the same window a year ago. Empty when year-ago data isn't available.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.revParImprovement30d", {
    id: "revParImprovement30d",
    header: headerTip(
      "RevPAR Δ",
      "Change in revenue per available room (RevPAR) over the last 30 days vs. the same window a year ago. Empty when year-ago data isn't available.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  // ---- Group 7: Health (Phase 2 — single column with chip + hover breakdown) ----
  // Uses accessorFn (not display) so TanStack Table treats `health.score`
  // as the underlying sort key — sortingFn alone on a display column
  // doesn't always wire `getCanSort()` correctly across versions.
  col.accessor((row) => row.health.score ?? -Infinity, {
    id: "health",
    header: headerTip(
      "Health",
      "Overall customer health on a 0-100 scale, blending 6 signals: sentiment, product performance, in-app rating, billing health, engagement, and onboarding.\nBands: healthy (85+) · watch (70-84) · concerning (50-69) · at-risk (<50).\nHover the bar for the per-signal breakdown.",
    ),
    enableSorting: true,
    sortingFn: "basic",
    cell: (info) => {
      const h = info.row.original.health;
      return (
        <HealthBandChip
          band={h.band}
          score={h.score}
          breakdown={h.componentBreakdown ?? null}
        />
      );
    },
  }),

  // ---- Group 8: Column-spec-revised additions (Primary + detail fields) ----
  col.accessor("pms", {
    id: "pms",
    header: headerTip(
      "PMS",
      "Which property management system the hotel uses (Cloudbeds, Mews, ResNexus, etc.). Empty when the customer hasn't connected a PMS yet.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (!v) return <span className="text-sm text-content-tertiary">—</span>;
      const label = PMS_LABEL[v.toLowerCase()] ?? v;
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-content-secondary text-xs font-medium whitespace-nowrap">
          {label}
        </span>
      );
    },
  }),

  col.display({
    id: "owners",
    header: headerTip(
      "Owner",
      "The CSM(s) assigned to this customer. Edit assignments from the 360 page.",
    ),
    enableSorting: true,
    // Sort by owner count, ties broken alphabetically by first owner email.
    sortingFn: (a, b) => {
      const oa = a.original.owners ?? [];
      const ob = b.original.owners ?? [];
      if (oa.length !== ob.length) return oa.length - ob.length;
      return (oa[0]?.adminEmail ?? "").localeCompare(ob[0]?.adminEmail ?? "");
    },
    cell: (info) => <OwnerStack owners={info.row.original.owners ?? []} />,
  }),

  col.accessor("billing.tenureMonths", {
    id: "tenureMonths",
    header: headerTip(
      "Tenure",
      "How long the customer has been with Autumn, in months.",
    ),
    enableSorting: true,
    cell: (info) => {
      const m = info.getValue();
      if (m == null) return <span className="text-sm text-content-tertiary">—</span>;
      if (m < 12) {
        return (
          <span className="text-sm text-content-primary">{m}mo</span>
        );
      }
      const years = Math.floor(m / 12);
      const months = m % 12;
      return (
        <span className="text-sm text-content-primary">
          {years}y{months > 0 ? ` ${months}mo` : ""}
        </span>
      );
    },
  }),

  col.accessor("billing.daysToRenewal", {
    id: "daysToRenewal",
    header: headerTip(
      "Days to Renewal",
      "Days remaining until contract renewal. Shows 'M2M' (month-to-month) when there's no fixed renewal term — contract auto-renews monthly.",
    ),
    enableSorting: true,
    cell: (info) => {
      const d = info.getValue();
      if (d == null) {
        return <span className="text-sm text-content-tertiary italic">M2M</span>;
      }
      const cls =
        d <= 30
          ? "text-brand-secondary font-medium"
          : d <= 60
            ? "text-yellow-700 font-medium"
            : "text-content-primary";
      return <span className={clsx("text-sm", cls)}>{d}d</span>;
    },
  }),

  col.accessor(
    (row) => {
      // Severity rank: red > yellow > green (highest risk on top when desc).
      const RANK: Record<string, number> = { green: 0, yellow: 1, red: 2 };
      return RANK[row.churnRisk.level] ?? 0;
    },
    {
      id: "churnRisk",
      header: headerTip(
        "Churn Risk",
        "Auto-computed from these triggers:\n1. Manual CSM flag (overrides everything)\n2. Most recent invoice failed\n3. Health band is concerning or at-risk\n4. Negative 30-day margin\nGreen = 0 triggers · Yellow = 1 · Red = 2+",
      ),
      enableSorting: true,
      sortingFn: "basic",
      cell: (info) => {
        const r = info.row.original.churnRisk;
        return <ChurnRiskBadge level={r.level} isManual={r.isManual} />;
      },
    },
  ),

  col.display({
    id: "marginTrend90d",
    header: headerTip(
      "Margin Trend (90d)",
      "Direction of this customer's 30-day margin over the last 90 days.\n▲ rising · ▼ falling · — flat or insufficient data.\nUses a linear-regression slope on daily margin snapshots; needs at least 3 days of history before it can render.",
    ),
    enableSorting: true,
    sortingFn: (a, b) =>
      (a.original.marginTrend90d?.slope ?? -Infinity) -
      (b.original.marginTrend90d?.slope ?? -Infinity),
    cell: (info) => (
      <MarginTrendIndicator trend={info.row.original.marginTrend90d} />
    ),
  }),

  col.display({
    id: "nextAction",
    header: headerTip(
      "Next Action",
      "The next thing a CSM has committed to doing for this customer (e.g., 'send Q2 review deck', 'follow up on renewal'). Set in the 360 page.",
    ),
    enableSorting: true,
    // Sort by due date — soonest-due first when ascending. Customers
    // without a next action drop to the end either direction.
    sortingFn: (a, b) => {
      const da = a.original.nextAction?.dueDate
        ? new Date(a.original.nextAction.dueDate).getTime()
        : Infinity;
      const db = b.original.nextAction?.dueDate
        ? new Date(b.original.nextAction.dueDate).getTime()
        : Infinity;
      return da - db;
    },
    cell: (info) => {
      const a = info.row.original.nextAction;
      return <NextActionPreview body={a.body} dueDate={a.dueDate} />;
    },
  }),

  col.display({
    id: "note",
    header: headerTip(
      "Notes",
      "Most recent admin note about this customer. Hover the cell for the full note body and total count.",
    ),
    enableSorting: true,
    // Sort by latest note timestamp; customers with no notes drop to the end.
    sortingFn: (a, b) => {
      const da = a.original.note?.createdAt
        ? new Date(a.original.note.createdAt).getTime()
        : -Infinity;
      const db = b.original.note?.createdAt
        ? new Date(b.original.note.createdAt).getTime()
        : -Infinity;
      return da - db;
    },
    cell: (info) => <NotePreview note={info.row.original.note} />,
  }),

  col.accessor("marketing.ctr", {
    id: "ctr",
    header: headerTip(
      "CTR",
      "Click-through rate: how often people click on the ad after seeing it. Higher = better creative + targeting.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.cac", {
    id: "cac",
    header: headerTip(
      "CAC",
      "Customer acquisition cost: average ad spend per booking. Lower = more efficient ad spend.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatCurrency(info.getValue())}
      </span>
    ),
  }),

  col.accessor("marketing.spendPacing", {
    id: "spendPacing",
    header: headerTip(
      "Spend Pacing",
      "How this month's ad spend compares to last month's. >1 = spending more · <1 = spending less. Catches budget overruns or under-pacing.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) return <span className="text-sm text-content-tertiary">—</span>;
      const pct = Math.round(v * 100);
      const cls =
        v > 1.1
          ? "text-brand-secondary"
          : v < 0.85
            ? "text-yellow-700"
            : "text-content-primary";
      return <span className={clsx("text-sm font-medium", cls)}>{pct}%</span>;
    },
  }),

  col.accessor("pricing.daysOnPricing", {
    id: "daysOnPricing",
    header: headerTip(
      "Days on Pricing",
      "How many days the customer has been live on Autumn's pricing engine. Empty for marketing-only customers.",
    ),
    enableSorting: true,
    cell: (info) => {
      const d = info.getValue();
      return (
        <span className="text-sm text-content-primary">
          {d == null ? "—" : `${d}d`}
        </span>
      );
    },
  }),

  col.accessor("pricing.revParYoY", {
    id: "revParYoY",
    header: headerTip(
      "RevPAR YoY",
      "Year-over-year change in revenue per available room (RevPAR). Compares the last 30 days against the same 30 days a year ago. Empty when we don't have a full year of PMS history.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) return <span className="text-sm text-content-tertiary">—</span>;
      const cls = v > 0 ? "text-brand-primary" : v < 0 ? "text-brand-secondary" : "";
      return (
        <span className={clsx("text-sm font-medium", cls)}>
          {v > 0 ? "+" : ""}
          {formatPercent(v)}
        </span>
      );
    },
  }),

  col.accessor("pricing.adrYoY", {
    id: "adrYoY",
    header: headerTip(
      "ADR YoY",
      "Year-over-year change in average daily rate (ADR). Compares the last 30 days against the same 30 days a year ago. Empty when year-ago data is missing.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("pricing.occupancyYoY", {
    id: "occupancyYoY",
    header: headerTip(
      "Occ YoY",
      "Year-over-year change in occupancy. Compares the last 30 days against the same 30 days a year ago. Empty when year-ago data is missing.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-primary">
        {formatPercent(info.getValue())}
      </span>
    ),
  }),

  col.accessor("pricing.revParSinceGoLive", {
    id: "revParSinceGoLive",
    header: headerTip(
      "RevPAR Since Go-Live",
      "Coming soon. Will show the lift in revenue per available room since the customer turned on Autumn's pricing engine, compared to the matched window a year before. Currently empty across the board.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) return <span className="text-sm text-content-tertiary">—</span>;
      const cls = v > 0 ? "text-brand-primary font-medium" : v < 0 ? "text-brand-secondary font-medium" : "";
      return (
        <span className={clsx("text-sm", cls)}>
          {v > 0 ? "+" : ""}
          {formatPercent(v)}
        </span>
      );
    },
  }),

  col.accessor("pricing.revenueLiftSinceGoLiveUsd", {
    id: "revenueLiftSinceGoLiveUsd",
    header: headerTip(
      "Revenue Lift $",
      "Coming soon. Companion to 'RevPAR Since Go-Live' — the dollar revenue lift attributable to Autumn's pricing engine since the customer went live. Currently empty across the board.",
    ),
    enableSorting: true,
    cell: (info) => {
      const v = info.getValue();
      if (v == null) return <span className="text-sm text-content-tertiary">—</span>;
      const cls = v > 0 ? "text-brand-primary font-medium" : v < 0 ? "text-brand-secondary font-medium" : "";
      return <span className={clsx("text-sm", cls)}>{formatCurrency(v)}</span>;
    },
  }),

  col.accessor("contract.endDate", {
    id: "contractEndDate",
    header: headerTip(
      "Contract End",
      "Date the contract ends. Empty = month-to-month with no fixed end date. Currently empty for all customers until contract data is backfilled from Stripe.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {formatDate(info.getValue())}
      </span>
    ),
  }),

  col.accessor("engagement.lastProactiveTouchAt", {
    id: "lastProactiveTouchAt",
    header: headerTip(
      "Last Proactive Touch",
      "When a CSM last manually reached out to this customer using the in-product Compose Email button. Empty until your team starts using it.",
    ),
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-content-secondary whitespace-nowrap">
        {relativeTime(info.getValue())}
      </span>
    ),
  }),

  col.accessor("engagement.inAppRating", {
    id: "inAppRating",
    header: headerTip(
      "In-App Rating",
      "Most recent in-product feedback rating from this customer (1-5 stars). Empty if they haven't submitted any feedback.",
    ),
    enableSorting: true,
    cell: (info) => {
      const r = info.getValue();
      if (r == null) return <span className="text-sm text-content-tertiary">—</span>;
      const cls =
        r >= 4 ? "text-brand-primary" : r >= 3 ? "text-yellow-700" : "text-brand-secondary";
      return (
        <span className={clsx("text-sm font-medium", cls)}>
          {r.toFixed(1)}★
        </span>
      );
    },
  }),
];

/**
 * Column groups by section per CPO revised spec. Primary is always visible;
 * Sections 2-5 are toggleable via the table's view switcher.
 *
 * The column definitions above stay grouped by domain for code readability;
 * these arrays dictate the runtime display order + visibility grouping.
 */

/**
 * Section 1 — Primary (always visible).
 * Fixed default order per user directive 2026-04-24:
 *   Hotel · Product · PMS · Health · ACV/ARR · Revenue · Spend · Margin · Margin Trend
 * — Revenue → Spend → Margin (Revenue − Spend) → Margin Trend follows the
 * mental model of "what came in, what went out, what's left, where it's
 * trending". Owners moves out of the primary skim view (still visible later).
 */
export const PRIMARY_COLUMN_IDS: string[] = [
  // Counter + identity (sticky-aligned at left of table)
  "rowNumber",
  "hotelName",
  // Identity-first cluster: # · Hotel · Test · PMS · Product · Plan so
  // Product (capsules) lands at col 5 and Plan at col 6 per directive
  // 2026-04-27. PMS slots in at col 4 to fill the gap.
  "testFlag",
  "pms",
  "products",
  "plan",
  "monthlyRevenue",
  "spend30d",
  "margin30d",
  "carr",
  "acvArr",
  "health",
  "lastCharge",
  "productType",
  "marginTrend90d",
  // Everything else follows
  "owners",
  "fixedFeeMonthly",
  "churnRisk",
  "lastActive",
  "needsReplyCount",
  "openTicketCount",
  "totalTicketCount",
  "daysToRenewal",
  "nextAction",
  "note",
];

/** Section 2 — Marketing Performance. */
export const MARKETING_COLUMN_IDS: string[] = [
  "impressions30d",
  "impressions7d",
  "impressionsWowDelta",
  "clicks30d",
  "ctr",
  "bookings30d",
  "bookingsValueUsd30d",
  "cac",
  "autumnShareOfTraffic",
  "autumnShareOfBookings",
  "brandProtectionPct",
  "brandProtectionBookings30d",
  "nonBrandProtectionBookings30d",
  "bookingsTrendDelta",
  "spendPacing",
  "revenue30d",
];

/** Section 3 — Pricing Performance (all_in_one only). */
export const PRICING_COLUMN_IDS: string[] = [
  "pricingGoLive",
  "daysOnPricing",
  "occupancyLift30d",
  "adrChange30d",
  "revParImprovement30d",
  "revParYoY",
  "adrYoY",
  "occupancyYoY",
  "revParSinceGoLive",
  "revenueLiftSinceGoLiveUsd",
];

/** Section 4 — Billing & Contract detail. (fixedFeeMonthly + plan + monthlyRevenue
 *  promoted to Primary; lastCharge replaces the latestChargeStatus + latestChargedAt
 *  pair, also in Primary.) */
export const BILLING_COLUMN_IDS: string[] = [
  "commissionRate",
  "totalCommissionYtd",
  "lastCommissionBill",
  "chargeCountAllTime",
  "paymentMethodStatus",
  "signedDate",
  "contractEndDate",
  "tenureMonths",
  "createdAt",
];

/** Section 5 — Engagement & Support detail. */
export const ENGAGEMENT_COLUMN_IDS: string[] = [
  "daysSinceLastEmail",
  "lastProactiveTouchAt",
  "ticketsThisMonth",
  "dashboardUnlockedAt",
  "inAppRating",
];

export type ColumnSection =
  | "primary"
  | "marketing"
  | "pricing"
  | "billing"
  | "engagement";

/**
 * Full left-to-right order when every section is on. Primary first, then
 * Marketing, Pricing, Billing, Engagement (matches CPO spec Section 1→5).
 */
export const DEFAULT_COLUMN_ORDER: string[] = [
  ...PRIMARY_COLUMN_IDS,
  ...MARKETING_COLUMN_IDS,
  ...PRICING_COLUMN_IDS,
  ...BILLING_COLUMN_IDS,
  ...ENGAGEMENT_COLUMN_IDS,
];

const SECTION_BY_COL_ID: Record<string, ColumnSection> = (() => {
  const map: Record<string, ColumnSection> = {};
  for (const id of PRIMARY_COLUMN_IDS) map[id] = "primary";
  for (const id of MARKETING_COLUMN_IDS) map[id] = "marketing";
  for (const id of PRICING_COLUMN_IDS) map[id] = "pricing";
  for (const id of BILLING_COLUMN_IDS) map[id] = "billing";
  for (const id of ENGAGEMENT_COLUMN_IDS) map[id] = "engagement";
  return map;
})();

/** Returns which section a column belongs to, or null when the id is unknown. */
export function getColumnSection(colId: string): ColumnSection | null {
  return SECTION_BY_COL_ID[colId] ?? null;
}

/**
 * True when `currentColId` is the last column of its section in the rendered
 * column order. Drives the visual separator border between sections.
 */
export function isSectionBoundary(
  currentColId: string,
  nextColId: string | null,
): boolean {
  if (!nextColId) return false;
  const a = getColumnSection(currentColId);
  const b = getColumnSection(nextColId);
  return a !== null && b !== null && a !== b;
}

/** Columns that should always remain visible (counter + identity sticky pair). */
export const REQUIRED_VISIBLE_COLUMN_IDS: ReadonlyArray<string> = [
  "rowNumber",
  "hotelName",
];

/**
 * Columns that render text-left (instead of the table-default text-right).
 * The counter + hotel-name sticky pair are always left; Plan / Products /
 * Product type / PMS read better as left-aligned labels too — they're
 * categorical/text values, not numbers.
 */
export const LEFT_ALIGNED_COLUMN_IDS: ReadonlySet<string> = new Set([
  "rowNumber",
  "hotelName",
  "plan",
  "products",
  "productType",
  "pms",
]);

/** Columns that render text-center — for boolean toggles like the Test checkbox. */
export const CENTER_ALIGNED_COLUMN_IDS: ReadonlySet<string> = new Set([
  "testFlag",
]);
